import { useEffect, useState } from "react";
import { MailCheck, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** Friendly prompt shown when an unverified account tries a higher-risk action
 *  (messaging, Requests, Wall posts/comments, reviews). The backend flags those
 *  403s with the X-Email-Verification-Required header; lib/api.js turns that
 *  into the `limya:email-verification-required` window event. */
export default function VerifyEmailPrompt() {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener("limya:email-verification-required", show);
    return () => window.removeEventListener("limya:email-verification-required", show);
  }, []);

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent — check your inbox.");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the email. Please try again.");
    }
    setSending(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/40 backdrop-blur-sm px-5"
      data-testid="verify-email-prompt">
      <div className="relative w-full max-w-sm bg-white rounded-2xl p-7 shadow-xl border border-black/5">
        <button onClick={() => setOpen(false)} aria-label="Close"
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-black/5"
          data-testid="verify-email-prompt-close">
          <X size={16} />
        </button>
        <MailCheck size={32} className="text-[#245544] mb-3" />
        <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>
          Please verify your email first
        </h2>
        <p className="text-sm text-[#222]/70 leading-relaxed mb-6">
          We use email verification to help keep LIMYA a little more trustworthy for everyone.
        </p>
        <button onClick={resend} disabled={sending}
          className="btn-lime w-full py-2.5 text-sm disabled:opacity-50"
          data-testid="verify-email-prompt-resend">
          {sending ? "Sending…" : "Resend verification email"}
        </button>
      </div>
    </div>
  );
}
